import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { useCallback } from 'react';
import { Platform } from 'react-native';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import { getDeviceId } from '../../lib/DeviceInfo';
import { captureException, captureMessage } from '../../logger/Logger';
import type { UserDevicePlatform } from '../types/Notifications';
import useRegisterDeviceForPushNotifications from './useRegisterDeviceForPushNotifications';

const useRegisterForPushNotifications = () => {
    const user = useAuthedUser();

    const { mutate: registerDevice, error: registerDeviceError } =
        useRegisterDeviceForPushNotifications();

    if (registerDeviceError) {
        captureException(registerDeviceError);
    }

    const registerForPushNotifications = useCallback(async () => {
        // Must be on a physical device for push notifications.
        if (!Device.isDevice) {
            if (IS_LOCAL) {
                captureMessage(
                    'Must use physical device for Push Notifications',
                );
            }
            return;
        }

        try {
            // Request push notifications permissions from OS
            await requestPushNotificationsPermissions();

            // Get expo token
            const expoPushToken = await getExpoPushToken();

            // Register the device with the backend
            const deviceId = await getDeviceId();
            registerDevice({
                body: {
                    deviceId,
                    pushToken: expoPushToken,
                    platform: Platform.OS.toUpperCase() as UserDevicePlatform,
                },
            });
        } catch (e) {
            captureException(
                new Error(
                    'Error either requesting push notifications permissions, getting expo push token, or registering device',
                ),
                { error: e, userId: user.id },
            );
        }
    }, []);

    return { registerForPushNotifications };
};

const requestPushNotificationsPermissions = async () => {
    const { status: existingStatus } =
        await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (finalStatus !== 'granted') {
        throw new Error('Failed to get push token for push notification.');
    }
};

const getExpoPushToken = async () => {
    // On Android 13, app users must opt-in to receive notifications via a permissions prompt automatically triggered by the operating system. This prompt will not appear until at least one notification channel is created. The setNotificationChannelAsync must be called before getDevicePushTokenAsync or getExpoPushTokenAsync to obtain a push token.
    if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('default', {
            name: 'default',
            importance: Notifications.AndroidImportance.MAX,
            vibrationPattern: [0, 250, 250, 250],
            lightColor: '#FF231F',
        });
    }

    const token = await Notifications.getExpoPushTokenAsync({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        projectId: Constants.expoConfig?.extra?.eas.projectId as string,
    });

    return token.data;
};

export default useRegisterForPushNotifications;
